import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Trash2, GripVertical, Plus, ArrowLeft } from "lucide-react";
import { LOGO, IMAGES, SERVICES, GALLERY } from "@/data/content";
import {
  adminLogin,
  fetchContent,
  saveContent,
  getToken,
  clearToken,
  verifyToken,
  resolveUrl,
} from "@/lib/api";
import ImageDrop from "@/components/admin/ImageDrop";

const seedGallery = () =>
  GALLERY.map((g, i) => ({ id: `default-${i}`, url: g.src, alt: g.alt || "" }));

const Section = ({ title, desc, children }) => (
  <section className="border border-white/10 bg-panel/50 p-6 sm:p-8">
    <h2 className="font-serif text-2xl text-cream">{title}</h2>
    {desc && <p className="text-cream/50 text-sm font-light mt-1 mb-6">{desc}</p>}
    {children}
  </section>
);

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);

  useEffect(() => {
    if (!authed) return;
    verifyToken().then((ok) => {
      if (!ok) {
        setAuthed(false);
        return;
      }
      fetchContent()
        .then((c) => {
          setContent({
            hero: c.hero || null,
            compareBefore: c.compareBefore || null,
            compareAfter: c.compareAfter || null,
            services: c.services || {},
            gallery: Array.isArray(c.gallery) ? c.gallery : seedGallery(),
          });
        })
        .catch(() => toast.error("Could not load content."));
    });
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await adminLogin(password);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
    setContent(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveContent(content);
      toast.success("Saved! Your website is updated.");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — please log in again.");
        logout();
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // gallery helpers
  const addGalleryImage = (url) =>
    setContent((c) => ({
      ...c,
      gallery: [...c.gallery, { id: crypto.randomUUID(), url, alt: "" }],
    }));
  const removeGalleryImage = (id) =>
    setContent((c) => ({ ...c, gallery: c.gallery.filter((g) => g.id !== id) }));
  const setAlt = (id, alt) =>
    setContent((c) => ({
      ...c,
      gallery: c.gallery.map((g) => (g.id === id ? { ...g, alt } : g)),
    }));
  const reorder = (from, to) =>
    setContent((c) => {
      const arr = [...c.gallery];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...c, gallery: arr };
    });

  // ---------------- Login screen ----------------
  if (!authed) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-4">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-panel border border-white/10 p-8"
        >
          <img src={LOGO} alt="MOTO LUXE" className="h-14 w-auto mx-auto mb-8" />
          <h1 className="font-serif text-3xl text-cream text-center">Editor Login</h1>
          <p className="text-center text-cream/50 text-sm mt-2 mb-6 font-light">
            Enter your password to manage photos.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-cream placeholder-cream/30 mb-8"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loggingIn}
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink py-3.5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-champagne transition-colors disabled:opacity-60"
          >
            {loggingIn && <Loader2 size={16} className="animate-spin" />}
            Enter
          </button>
          <Link
            to="/"
            className="mt-6 flex items-center justify-center gap-2 text-cream/40 hover:text-gold text-xs font-mono tracking-[0.15em] uppercase"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="text-gold animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-cream">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-ink/90 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="MOTO LUXE" className="h-9 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-gold/70 uppercase hidden sm:inline">
              Photo Editor
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 border border-white/20 text-cream/80 hover:border-gold hover:text-gold px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors"
            >
              View Site
            </Link>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-champagne transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Changes
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="text-cream/50 hover:text-gold p-2"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <Section title="Hero Banner" desc="The big image at the top of your homepage.">
          <ImageDrop
            testid="drop-hero"
            value={content.hero}
            fallback={IMAGES.hero}
            aspect="aspect-[16/7]"
            onUploaded={(url) => setContent((c) => ({ ...c, hero: url }))}
          />
        </Section>

        <Section title="Service Photos" desc="One photo for each detailing package.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <ImageDrop
                key={s.id}
                testid={`drop-service-${s.id}`}
                label={s.name}
                value={content.services?.[s.id] || null}
                fallback={s.image}
                aspect="aspect-[4/3]"
                onUploaded={(url) =>
                  setContent((c) => ({
                    ...c,
                    services: { ...c.services, [s.id]: url },
                  }))
                }
              />
            ))}
          </div>
        </Section>

        <Section title="Before / After" desc="Shown in the comparison slider.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ImageDrop
              testid="drop-before"
              label="Before"
              value={content.compareBefore}
              fallback={IMAGES.compare}
              aspect="aspect-video"
              onUploaded={(url) => setContent((c) => ({ ...c, compareBefore: url }))}
            />
            <ImageDrop
              testid="drop-after"
              label="After"
              value={content.compareAfter}
              fallback={IMAGES.compare}
              aspect="aspect-video"
              onUploaded={(url) => setContent((c) => ({ ...c, compareAfter: url }))}
            />
          </div>
        </Section>

        <Section title="Gallery" desc="Add, remove and drag to reorder your gallery photos.">
          <div
            data-testid="admin-gallery-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {content.gallery.map((g, i) => (
              <div
                key={g.id}
                data-testid={`admin-gallery-item-${i}`}
                draggable
                onDragStart={() => (dragIndex.current = i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIndex.current !== null && dragIndex.current !== i) {
                    reorder(dragIndex.current, i);
                  }
                  dragIndex.current = null;
                }}
                className="group relative border border-white/10 bg-panel overflow-hidden cursor-grab active:cursor-grabbing"
              >
                <img
                  src={resolveUrl(g.url)}
                  alt={g.alt}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute top-1.5 left-1.5 bg-ink/70 text-cream/70 p-1 rounded">
                  <GripVertical size={14} />
                </div>
                <button
                  data-testid={`admin-gallery-delete-${i}`}
                  onClick={() => removeGalleryImage(g.id)}
                  className="absolute top-1.5 right-1.5 bg-ink/70 text-cream/80 hover:text-red-400 p-1 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
                <input
                  value={g.alt}
                  onChange={(e) => setAlt(g.id, e.target.value)}
                  placeholder="Caption (optional)"
                  className="w-full bg-ink/80 text-[11px] text-cream/70 px-2 py-1.5 outline-none border-t border-white/10 placeholder-cream/25"
                />
              </div>
            ))}

            {/* Add tile */}
            <ImageDrop
              testid="drop-gallery-add"
              value={null}
              fallback={null}
              aspect="h-40"
              onUploaded={addGalleryImage}
            />
          </div>
          <p className="mt-4 text-cream/40 text-xs font-light flex items-center gap-2">
            <Plus size={12} /> Drop images on the last tile to add more. Drag any photo to reorder.
          </p>
        </Section>

        <div className="flex justify-end pb-10">
          <button
            data-testid="admin-save-button-bottom"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-3.5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-champagne transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
